/**
 * rank.js — shorten a text to a size you name, by choosing sentences.
 *
 * WHAT THE JOB ACTUALLY IS. The query behind this tool is «сократить текст без
 * потери смысла», and the situation behind the query is almost always a LIMIT:
 * an essay capped at 2000 characters, an abstract capped at 250 words, a field
 * that refuses more. So the target here is a NUMBER the visitor types, not a
 * "short / medium / long" dial — a dial cannot answer "make this fit in 1800
 * characters", which is the only question actually being asked.
 *
 * WHY EXTRACTIVE, AND WHAT THAT COSTS. Sentences are kept whole, chosen by how
 * central they are to the text. The gain is that every word in the result was
 * written by the author: nothing is invented, no fact is bent, and the output
 * can be checked by eye against the source. The cost is stated rather than
 * hidden — this cannot compress WITHIN a sentence, so a text of three very long
 * sentences cannot be cut to a quarter by any amount of selection. The tool
 * says so when it happens instead of quietly missing the target.
 *
 * THE PART EVERY RUSSIAN TEXT TOOL GETS WRONG is the very first step: deciding
 * where a sentence ends. A full stop is not a sentence boundary in «т. е.»,
 * «и т. д.», «рис. 4», «стр. 12», «А. С. Пушкин», «п. 3.2» or «1985 г.» — and a
 * splitter that ignores this produces fragments, ranks fragments, and returns
 * fragments. The abbreviation list below is the working part of this file, not
 * decoration; it is tested on those exact shapes.
 *
 * Exposed as window.TSRank.
 */
(function (root) {
    'use strict';
    if (root.TSRank) return;

    /*
     * СОКРАЩЕНИЯ БЫВАЮТ ДВУХ РОДОВ, и одним списком их не разделить.
     *
     * «т.», «рис.», «им.», инициал «А.» не кончают предложение НИКОГДА — за
     * ними всегда идёт продолжение конструкции. А «руб.», «млн», «г.», «тыс.»
     * кончают его сплошь и рядом: «цена равна 1.5 млн руб. Оплата в срок.» —
     * и в одном списке они дают выбор между двумя поломками: либо «руб.»
     * склеивает два предложения, либо «т. е.» их рвёт.
     *
     * Различает их то, что ИДЁТ СЛЕДОМ. У сокращения-единицы заглавная буква
     * после точки — настоящая граница; у «т.» и инициала — нет. Строчная буква
     * после «и т. д.» так же надёжно говорит, что предложение продолжается.
     */
    var NEVER_FINAL = ('т|е|к|см|ср|рис|табл|стр|им|ул|кв|обл|проф|акад|доц|канд|гл|разд|'
        + 'ст|пп|абз|прим|прил|доп|изд|перераб|соавт|отв|ред|сост|пер|яз|напр|мн|ч|'
        + 'mr|mrs|dr|prof|fig|eq|ch|vol|no|st|jr|sr|approx|dept|univ').split('|');
    /* Единицы и «и т. д.» — заглавная после них кончает предложение. */
    var MAY_BE_FINAL = ('руб|коп|шт|экз|тыс|млн|млрд|г|гг|в|вв|с|д|п|др|пр|н|э|'
        + 'etc|vs|pp|inc|ltd|co|al').split('|');
    var NEVER_SET = {}, MAYBE_SET = {};
    NEVER_FINAL.forEach(function (a) { NEVER_SET[a] = 1; });
    MAY_BE_FINAL.forEach(function (a) { MAYBE_SET[a] = 1; });

    /* Stop words carry no topic. Without removing them the highest-scoring
       sentence is simply the longest one, because "и" and "в" dominate every
       count. Russian + English, because pasted text is routinely mixed. */
    var STOP = ('и|в|во|не|что|он|на|я|с|со|как|а|то|все|она|так|его|но|да|ты|к|у|же|вы|за|бы|по|'
        + 'только|ее|мне|было|вот|от|меня|еще|нет|о|из|ему|теперь|когда|даже|ну|вдруг|ли|если|'
        + 'уже|или|ни|быть|был|него|до|вас|нибудь|опять|уж|вам|ведь|там|потом|себя|ничего|ей|'
        + 'может|они|тут|где|есть|надо|ней|для|мы|тебя|их|чем|была|сам|чтоб|без|будто|чего|раз|'
        + 'тоже|себе|под|будет|ж|тогда|кто|этот|того|потому|этого|какой|совсем|ним|здесь|этом|'
        + 'один|почти|мой|тем|чтобы|нее|сейчас|были|куда|зачем|всех|никогда|можно|при|наконец|'
        + 'два|об|другой|хоть|после|над|больше|тот|через|эти|нас|про|всего|них|какая|много|'
        + 'разве|эту|моя|впрочем|хорошо|свою|этой|перед|иногда|лучше|чуть|том|нельзя|такой|'
        + 'им|более|всегда|конечно|всю|между|это|её|его|также|весь|наш|ваш|свой|который|которая|'
        + 'которые|которых|котором|the|a|an|and|or|but|if|of|to|in|on|at|for|with|as|by|from|'
        + 'is|are|was|were|be|been|being|it|its|this|that|these|those|he|she|they|we|you|i|not|'
        + 'no|do|does|did|have|has|had|will|would|can|could|should|may|might|there|their|then|'
        /* Служебные слова остальных языков сайта. Без них центральность у
           немецкого или индонезийского текста считалась по «der», «und»,
           «yang», и самым «важным» выходило просто самое длинное предложение —
           тот же дефект, от которого русский и английский были защищены. */
        + 'і|й|та|але|що|це|як|не|на|в|у|з|із|до|від|для|по|за|про|при|він|вона|воно|вони|ми|ви|'
        + 'його|її|їх|був|була|було|були|бути|є|також|тому|цей|ця|ці|той|яка|який|які|якщо|коли|'
        + 'der|die|das|und|oder|aber|ist|sind|war|nicht|ein|eine|einen|dem|den|des|zu|von|mit|für|'
        + 'auf|im|in|an|es|sich|auch|als|wie|wird|werden|bei|nach|aus|dass|'
        + 'el|la|los|las|un|una|y|o|pero|es|son|de|del|en|con|por|para|que|se|lo|su|sus|como|más|al|'
        + 'le|les|et|ou|mais|est|sont|des|du|une|dans|pour|qui|sur|avec|ce|cette|pas|au|aux|il|elle|'
        + 'os|as|um|uma|e|não|do|da|dos|das|em|na|no|nas|nos|com|por|para|mais|ao|ele|ela|'
        + 'yang|dan|di|ini|itu|dengan|untuk|dari|tidak|ada|akan|dalam|juga|pada|ke|atau|karena|bisa'
    ).split('|');
    var STOP_SET = {};
    STOP.forEach(function (w) { STOP_SET[w] = 1; });

    // ── sentences ────────────────────────────────────────────────────────

    /* Что начинает предложение. До 2026-10-02 здесь стоял `[А-ЯЁA-Z0-9]` — и
       три поломки разом: украинские «І», «Є», «Ї», «Ґ» лежат вне диапазона
       А–Я, поэтому «…зростання. Інвестиції…» не делилось; французское «À» и
       испанские «¿» «¡» не начинали предложения; а у корейского и арабского
       нет заглавных вовсе, и весь текст на этих языках был ОДНИМ
       предложением — выбирать было не из чего. Теперь: любая заглавная,
       любая буква письменности без регистра, цифра. */
    /* После тире реплики пробел обычен («— Ты ли это?»): до 2026-10-03 он
       ломал проверку, и реплики в абзаце Чехова слипались в одно
       «предложение». */
    var STARTS = /^(?:["'«“„(\[¿¡]|[-—–][ \t ]*)?(?:\p{Lu}|\p{Lt}|\p{Lo}|\p{N})/u;
    /* Следом идёт новая реплика: тире и заглавная — «.- Глупости!», «. — Ты». */
    var NEXT_REPLICA = /^[ \t ]*["»”’)]*[ \t ]*[-—–][ \t ]*(?:\p{Lu}|\p{Lt}|\p{Lo})/u;
    /* Маркер пункта списка в начале строки. */
    var BULLET = /^[ \t]*(?:[-–—•*·▪●◦‣]|\d{1,3}[.)])[ \t]+\S/;
    var TERMINATORS = '.!?…؟。！？';

    /**
     * Does the stop at `dotIndex` belong to an abbreviation rather than to the
     * end of a sentence? `after` is the text that follows it.
     */
    function isAbbrBefore(text, dotIndex, after) {
        var i = dotIndex - 1, word = '';
        while (i >= 0 && /[^\s(«"'\[]/.test(text[i])) { word = text[i] + word; i--; }
        if (!word) return false;
        var low = word.toLowerCase().replace(/\.$/, '');

        // An initial is one capital letter and a stop: «А. С. Пушкин».
        if (/^\p{Lu}$/u.test(word)) return true;
        if (NEVER_SET[low]) return true;
        if (MAYBE_SET[low]) {
            // A unit ends the sentence when a new one visibly begins.
            var head = String(after || '').replace(/^\s+/, '');
            var startsSentence = STARTS.test(head) || head === '';
            return !startsSentence;
        }
        return false;
    }

    /**
     * Знак без пробела перед заглавной — граница предложения или нет?
     * Да — когда перед знаком обычное строчное слово из двух букв и больше
     * (не сокращение и не инициал), а дальше заглавная со строчной: «дому.Долго»,
     * «девочка?Та». Нет — «г.Москва», «т.е.», «U.S.», «ver.2», «site.Com».
     */
    function gluedStart(text, i, c, after) {
        // Заглавная со строчной («Долго»), однобуквенное слово («С тех пор»),
        // слово капителью («КОНЕЦ») или буква письменности без регистра.
        if (!/^(?:\p{Lu}(?:\p{Ll}|[ \t ])|\p{Lu}{3,}|\p{Lo})/u.test(after)) return false;
        if (c !== '.') return true;
        var k = i - 1, word = '';
        while (k >= 0 && /\p{L}/u.test(text[k])) { word = text[k] + word; k--; }
        // Строчное слово из двух букв и больше — или имя с заглавной из трёх
        // («крикнул Петя.Он»); «Св.», «США.», инициалы — не граница.
        if (!(word.length >= 2 && /^\p{Ll}+$/u.test(word)) && !(word.length >= 3 && /^\p{Lu}\p{Ll}+$/u.test(word))) return false;
        if (k >= 0 && /[\/@.\d_-]/.test(text[k])) return false;      // адрес, версия, почта
        var low = word.toLowerCase();
        return !NEVER_SET[low] && !MAYBE_SET[low];
    }

    /**
     * split(text) → [{ text, start, end, para }]
     *
     * A paragraph break always ends a sentence; inside a paragraph the stop has
     * to survive the abbreviation test above and be followed by something that
     * starts a sentence.
     *
     * INVARIANT: text.slice(start, end) === sentence.text. It has to hold,
     * because everything that edits INSIDE a sentence (trim.js) addresses the
     * source by absolute offset. Until 2026-08-26 `start` pointed at the space
     * BEFORE the sentence — `.trim()` moved the text but not the offset — so
     * every intra-sentence edit landed one character late and produced
     * «СЕреход» out of «Стоит отметить, что переход». It cost nothing while
     * only whole sentences were selected, which is exactly why it survived.
     */
    function split(text) {
        var s = String(text || '').replace(/\r\n?/g, '\n');
        var out = [];
        var paras = s.split(/\n{2,}/);
        var offset = 0;

        paras.forEach(function (para, pi) {
            var base = s.indexOf(para, offset);
            if (base < 0) base = offset;
            offset = base + para.length;

            var last = 0;
            for (var i = 0; i < para.length; i++) {
                var c = para[i];
                /* Перевод строки ВНУТРИ абзаца — граница, если это список или
                   заголовок. Пункты «— срок», «— бюджет» без точек иначе
                   склеивались в одно «предложение» на весь список, и выбрасывать
                   его можно было только целиком. Строка, оборванная переносом
                   (копия из PDF), границей не считается: за ней строчная буква. */
                if (c === '\n') {
                    var lineAt = para.lastIndexOf('\n', i - 1) + 1;
                    var prevLine = para.slice(lineAt, i).trim();
                    /* Заголовок — короткая строка без знака в конце, стоящая
                       первой в абзаце или после законченной фразы. Без второго
                       условия строка PDF, оборванная перед именем («встретились
                       с\nИваном»), резалась бы как заголовок. */
                    var beforeLine = para.slice(0, lineAt).replace(/\s+$/, '');
                    /* …и строка не кончается коротким служебным словом: «с»,
                       «и», «на», «of», «the» в конце строки — почти всегда
                       оборванная фраза, а не заголовок. */
                    var headingSlot = (!beforeLine || /[.!?…:;]$/.test(beforeLine))
                        && !/(?:^|\s)\p{Ll}{1,3}$/u.test(prevLine);
                    var nextLine = para.slice(i + 1);
                    var nl = nextLine.indexOf('\n');
                    var nextHead = (nl < 0 ? nextLine : nextLine.slice(0, nl)).trim();
                    var isBreak = !!prevLine && !!nextHead && (
                        BULLET.test(nextLine)
                        || (BULLET.test(prevLine) && !/[,;]$/.test(prevLine))
                        || (/[:;]$/.test(prevLine) && STARTS.test(nextHead))
                        || (headingSlot && prevLine.length <= 60 && !/[.!?…,;:—–-]$/.test(prevLine)
                            && STARTS.test(nextHead))
                    );
                    if (!isBreak) continue;
                    var rawL = para.slice(last, i);
                    var pieceL = rawL.trim();
                    if (pieceL) {
                        var atL = base + last + rawL.indexOf(pieceL);
                        out.push({ text: pieceL, start: atL, end: atL + pieceL.length, para: pi });
                    }
                    last = i + 1;
                    continue;
                }
                /* Двоеточие перед репликой в тексте, где переводы строк потерялись
                   при копировании («говорит:- Алёнушка, милая…»): слова автора и
                   реплика — разные единицы, иначе их можно только выбросить вместе. */
                if (c === ':') {
                    if (!NEXT_REPLICA.test(para.slice(i + 1))) continue;
                    var rawC = para.slice(last, i + 1);
                    var pieceC = rawC.trim();
                    if (pieceC) {
                        var atC = base + last + rawC.indexOf(pieceC);
                        out.push({ text: pieceC, start: atC, end: atC + pieceC.length, para: pi });
                    }
                    last = i + 1;
                    continue;
                }
                if (TERMINATORS.indexOf(c) < 0) continue;
                // Run of terminators: "?!.." counts once.
                var j = i;
                while (j + 1 < para.length && TERMINATORS.indexOf(para[j + 1]) >= 0) j++;
                /* Закрывающая кавычка или скобка после знака — часть того же
                   предложения: «…сказал он».» */
                while (j + 1 < para.length && /["»”’)\]]/.test(para[j + 1])) j++;
                var after = para.slice(j + 1);
                // A digit right after a stop is a number, not a new sentence.
                if (/^\s*\d/.test(after) && c === '.') { i = j; continue; }
                if (c === '.' && isAbbrBefore(para, i, after)) { i = j; continue; }
                var replica = NEXT_REPLICA.test(after);
                /* Знак, приклеенный к следующему предложению: «из дому.Долго»,
                   «девочка?Та ей отвечает». Так выглядит почти любой текст,
                   скопированный с сайта, где абзацы были тегами: переводы строк
                   пропали, пробелы — тоже. До 2026-10-03 такие места не делились,
                   и сцена диалога в 600 знаков становилась одним «предложением». */
                var glued = c !== '。' && !/^(\s|$)/.test(after);
                if (glued && !replica && !gluedStart(para, i, c, after)) { i = j; continue; }
                if (!glued && !replica && after.trim() && !STARTS.test(after.trim())) { i = j; continue; }

                var raw = para.slice(last, j + 1);
                var piece = raw.trim();
                if (piece) {
                    var at = base + last + raw.indexOf(piece);
                    out.push({ text: piece, start: at, end: at + piece.length, para: pi });
                }
                last = j + 1;
                i = j;
            }
            var rawTail = para.slice(last);
            var tail = rawTail.trim();
            if (tail) {
                var tailAt = base + last + rawTail.indexOf(tail);
                out.push({ text: tail, start: tailAt, end: tailAt + tail.length, para: pi });
            }
        });
        return out;
    }

    // ── scoring ──────────────────────────────────────────────────────────

    function words(s) {
        return String(s).toLowerCase()
            .replace(/ё/g, 'е')
            .match(/[\p{L}\p{N}][\p{L}\p{N}-]*/gu) || [];
    }

    /**
     * Crude but effective stemming for Russian: drop the common inflectional
     * tails so «документа», «документу» and «документов» count as one term.
     * A real morphological analyser would be better and is 3 MB; this costs
     * nothing and recovers most of the benefit for a frequency count.
     */
    /* Окончания на -ии/-ия/-ию/-ей/-ием добавлены 2026-10-02: без них
       «компании» и «компаний», «согласование» и «согласовании» считались
       РАЗНЫМИ терминами, и вывод текста про согласование договоров выходил
       «не про то». Усечение основы до N букв пробовали — оно склеивает
       «документ» с «документооборотом», то есть разные понятия. */
    function stem(w) {
        if (w.length <= 4) return w;
        return w.replace(/(иями|ями|ами|иях|ием|иям|ях|ов|ев|ий|ый|ой|ая|яя|ое|ее|ые|ие|ии|ия|ию|ей|ах|ам|ям|ом|ем|ых|их|ую|юю|ся|сь|ла|ло|ли|ть|ет|ут|ют|ит|ат|ят|а|я|о|е|у|ю|ы|и|й|ь)$/,
            function (m, _t, off) { return (off >= 4) ? '' : m; });
    }

    function termFreq(sentences) {
        var df = {}, tf = [];
        sentences.forEach(function (s) {
            var seen = {}, counts = {};
            words(s.text).forEach(function (w) {
                if (STOP_SET[w]) return;
                var t = stem(w);
                if (t.length < 3) return;
                counts[t] = (counts[t] || 0) + 1;
                if (!seen[t]) { seen[t] = 1; df[t] = (df[t] || 0) + 1; }
            });
            tf.push(counts);
        });
        return { df: df, tf: tf };
    }

    /**
     * score(sentences) → [number]
     *
     * CENTRALITY, NOT RARITY. The obvious scoring — add up TF-IDF weights —
     * rewards a sentence for containing unusual words, and a sentence that is
     * OFF-TOPIC contains nothing but unusual words. Measured on the tool's own
     * fixture: an article about electronic documents kept «Погода вчера была
     * ясной, а сегодня синоптики обещают затяжной дождь» in a four-sentence
     * summary, because every term in it («погода», «синоптики», «затяжной»)
     * appears exactly once and therefore scores maximum IDF. The cat sleeping
     * on the windowsill scored the same way.
     *
     * So the score is how much a sentence resembles the REST of the text:
     * cosine similarity of TF-IDF vectors, summed over the other sentences.
     * A sentence about documents in a text about documents resembles many
     * others; a sentence about the weather resembles none, and its rare words
     * now count against it instead of for it. This is the degree-centrality
     * half of TextRank — the power iteration adds little on texts of this size
     * and costs a loop nobody would be able to explain from the output.
     */
    function score(sentences) {
        var N = sentences.length || 1;
        var f = termFreq(sentences);
        var firstOfPara = {};
        sentences.forEach(function (s, i) {
            if (firstOfPara[s.para] === undefined) firstOfPara[s.para] = i;
        });

        // TF-IDF vectors, L2-normalised so the cosine is a plain dot product.
        var vecs = f.tf.map(function (counts) {
            var v = {}, norm = 0;
            Object.keys(counts).forEach(function (t) {
                var w = counts[t] * Math.log(1 + N / (1 + f.df[t]));
                v[t] = w;
                norm += w * w;
            });
            norm = Math.sqrt(norm) || 1;
            Object.keys(v).forEach(function (t) { v[t] /= norm; });
            return v;
        });

        function cos(a, b) {
            var keys = Object.keys(a), sum = 0;
            for (var i = 0; i < keys.length; i++) {
                var o = b[keys[i]];
                if (o) sum += a[keys[i]] * o;
            }
            return sum;
        }

        /* Сумма косинусов со ВСЕМИ остальными — это скалярное произведение с
           суммой всех векторов минус косинус с самим собой (векторы
           нормированы, он равен 1). Тот же ответ, но за N шагов вместо N²:
           глава на 600 предложений считалась 0.6 с, теперь — миллисекунды. */
        var total = {};
        vecs.forEach(function (v) {
            Object.keys(v).forEach(function (t) { total[t] = (total[t] || 0) + v[t]; });
        });

        return sentences.map(function (s, i) {
            var len = words(s.text).length || 1;
            if (!Object.keys(vecs[i]).length) return 0;
            var v = cos(vecs[i], total) - cos(vecs[i], vecs[i]);
            v /= Math.max(1, N - 1);
            // A paragraph's opening carries its claim far more often than its
            // middle does — a mild nudge, not a rule.
            if (firstOfPara[s.para] === i) v *= 1.18;
            // A five-word sentence is rarely the point of a text, and picking it
            // wastes budget that a real claim needed.
            if (len < 5) v *= 0.55;
            return v;
        });
    }

    // ── selection ────────────────────────────────────────────────────────

    /** Значимые основы фразы — то, по чему overlap() сравнивает повторы. */
    function terms(s) {
        var out = [];
        words(s).forEach(function (w) { if (!STOP_SET[w]) out.push(stem(w)); });
        return out;
    }

    function overlap(a, b) {
        var A = {}, n = 0, hit = 0;
        words(a).forEach(function (w) { if (!STOP_SET[w]) { A[stem(w)] = 1; } });
        words(b).forEach(function (w) {
            if (STOP_SET[w]) return;
            n++;
            if (A[stem(w)]) hit++;
        });
        return n ? hit / n : 0;
    }

    /**
     * shorten(text, opts) → { text, kept, dropped, chars, target, sentences, shortfall }
     *
     * `opts.target` is a size in `opts.unit` ('chars' | 'words' | 'percent').
     * Sentences are taken best-first, with a redundancy penalty so a paraphrase
     * of something already chosen does not spend the budget twice, and then put
     * back into the ORIGINAL ORDER — a summary that reorders the author's
     * argument is a different text, not a shorter one.
     */
    function shorten(text, opts) {
        opts = opts || {};
        var sentences = split(text);
        if (!sentences.length) {
            return { text: '', kept: [], dropped: [], chars: 0, target: 0, sentences: [], shortfall: 0 };
        }

        var unit = opts.unit || 'chars';
        var srcChars = String(text).length;
        var srcWords = words(text).length;
        var target;
        if (unit === 'percent') target = Math.max(1, Math.round(srcChars * (opts.target || 30) / 100));
        else if (unit === 'words') target = Math.max(1, opts.target || 200);
        else target = Math.max(1, opts.target || 1000);

        var sizeOf = function (s) { return unit === 'words' ? words(s.text).length : s.text.length + 1; };

        var scores = score(sentences);
        var order = sentences.map(function (s, i) { return i; })
            .sort(function (a, b) { return scores[b] - scores[a]; });

        /* Постороннее (центральность меньше четверти медианы) не заполняет
           остаток бюджета: короткая фраза про погоду влезает туда лучше
           длинного вывода, и рюкзак заполнялся мусором. */
        var med = scores.slice().sort(function (a, b) { return a - b; })[Math.floor(scores.length / 2)] || 0;
        var chosen = [], used = 0;
        var chosenText = '';
        for (var k = 0; k < order.length; k++) {
            var i = order[k];
            var sz = sizeOf(sentences[i]);
            if (used + sz > target && chosen.length) continue;
            if (chosen.length && scores[i] < med * 0.25) continue;
            // Redundancy: a sentence that repeats what is already in is not
            // worth its size, however central its terms are.
            if (chosen.length && overlap(chosenText, sentences[i].text) > 0.72) continue;
            chosen.push(i);
            chosenText += ' ' + sentences[i].text;
            used += sz;
            if (used >= target) break;
        }
        if (!chosen.length) chosen = [order[0]];

        chosen.sort(function (a, b) { return a - b; });
        var keptSet = {};
        chosen.forEach(function (i) { keptSet[i] = 1; });

        // Paragraph shape is meaning: joining every kept sentence into one block
        // turns an argument into a wall. Breaks are preserved where the source
        // had them.
        var parts = [], lastPara = null;
        chosen.forEach(function (i) {
            var s = sentences[i];
            if (lastPara !== null && s.para !== lastPara) parts.push('\n\n');
            else if (parts.length) parts.push(' ');
            parts.push(s.text);
            lastPara = s.para;
        });
        var outText = parts.join('');

        return {
            text: outText,
            kept: chosen,
            dropped: sentences.map(function (_, i) { return i; }).filter(function (i) { return !keptSet[i]; }),
            chars: outText.length,
            words: words(outText).length,
            srcChars: srcChars,
            srcWords: srcWords,
            target: target,
            unit: unit,
            sentences: sentences,
            scores: scores,
            /* How much the target was missed by, and it CAN be missed: the
               shortest possible extract is one sentence, so a text of three long
               sentences cannot reach a quarter of its size. Reported instead of
               silently overshooting. */
            shortfall: Math.max(0, (unit === 'words' ? words(outText).length : outText.length) - target),
        };
    }

    /** Rebuild the output from an explicit set of sentence indices (the UI's toggles). */
    function assemble(sentences, indices) {
        var idx = indices.slice().sort(function (a, b) { return a - b; });
        var parts = [], lastPara = null;
        idx.forEach(function (i) {
            var s = sentences[i];
            if (!s) return;
            if (lastPara !== null && s.para !== lastPara) parts.push('\n\n');
            else if (parts.length) parts.push(' ');
            parts.push(s.text);
            lastPara = s.para;
        });
        return parts.join('');
    }

    root.TSRank = {
        split: split,
        score: score,
        shorten: shorten,
        assemble: assemble,
        _words: words,
        _stem: stem,
        _overlap: overlap,
        _terms: terms,
    };
})(typeof self !== 'undefined' ? self : this);
